import * as React from "react";
import { observer } from "mobx-react";
import { UitkCardAloha, UitkCardAlohaContentSection, UitkCardAlohaLink, UitkCardMedia } from "uitk-react-cards";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkPriceLockup, UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { QueryRoute, RouteStore } from "bernie-core";
import { Link } from "react-router-dom";
import { Layer } from "bernie-view";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { UitkSpacing } from "uitk-react-spacing";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkBadge } from "uitk-react-badge";
import { UitkHeading, UitkText } from "uitk-react-text";
import { withStores } from "stores";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;

export interface ActivityInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  border?: boolean;
  routeStore: RouteStore;
}
export const ActivityInventoryCard = withStores("routeStore")(
  observer(
    ({ offer, cardIndex, campaignId, closeDialog, moduleName, border, routeStore }: ActivityInventoryCardProps) => {
      if (offer.__typename !== "ActivityOfferItem") {
        return null;
      }
      const discountBadge = offer.badge?.text;
      const isFreeCancellation = offer.freeCancellationMessage;
      const { formatText } = useLocalization();
      const strikeThroughPrice = offer?.activityOffer?.strikeThroughPrice?.value;
      const currentPrice = offer.activityOffer?.price?.value;
      const a11yPrice = offer.activityOffer?.price?.allyText || "";
      const priceDisclaimerLabel = formatText(locKeys.priceDisclaimerLabel);

      return (
        <UitkCardAloha className="ActivityInventoryCard" border={border} overflow={false}>
          <UitkCardMedia>
            <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
              <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
            </UitkFigure>
          </UitkCardMedia>
          <div>
            <UitkCardAlohaContentSection>
              <div>
                <UitkSpacing padding={{ blockend: "half" }}>
                  <UitkHeading level={3} size={500} className="truncate-2-lines">
                    {offer.title}
                  </UitkHeading>
                </UitkSpacing>
                <span className="uitk-type-300">{offer.province}</span>
              </div>
            </UitkCardAlohaContentSection>
            <UitkCardAlohaContentSection>
              <div>
                {!discountBadge ? (
                  <UitkSpacing padding={{ blockend: "six" }} margin={{ block: "unset" }}>
                    <hr />
                  </UitkSpacing>
                ) : (
                  <>
                    <UitkSpacing margin={{ block: "unset" }}>
                      <hr className="separatorLineWidth" />
                    </UitkSpacing>
                    <UitkLayoutPosition type="relative" position={{ top: "minus-three" }}>
                      <div className="uitk-type-right">
                        <UitkBadge text={discountBadge} />
                      </div>
                    </UitkLayoutPosition>
                  </>
                )}
              </div>

              <UitkLayoutGrid columns={2} space="two">
                <>
                  <div>
                    {offer.freeCancellationMessage && (
                      <UitkSpacing padding={{ blockend: "two" }}>
                        <UitkText theme="positive" size={300} weight="bold">
                          {offer?.freeCancellationMessage}
                        </UitkText>
                      </UitkSpacing>
                    )}
                    <UitkLayoutPosition
                      type="relative"
                      position={isFreeCancellation ? undefined : { bottom: "minus-six" }}
                    >
                      <div className="uitk-type-200 dateStyle">
                        {offer?.duration && (
                          <UitkLayoutFlexItem className="uitk-type-200">
                            <UitkLayoutFlex alignItems="center">
                              <UitkSpacing margin={{ inlineend: "one" }}>
                                <span>{offer?.durationLabel}</span>
                              </UitkSpacing>
                              <UitkSpacing margin={{ inlineend: "one" }}>
                                <UitkIcon name="access_time" size={UitkIconSize.EXTRA_SMALL} id="access_time" />
                              </UitkSpacing>
                              <span>{offer?.duration}</span>
                            </UitkLayoutFlex>
                          </UitkLayoutFlexItem>
                        )}
                      </div>
                    </UitkLayoutPosition>
                  </div>
                  <UitkLayoutPosition position={{ zindex: "layer2" }}>
                    <div className="price">
                      <UitkPriceLockup
                        align={UitkPriceLockupAlign.RIGHT}
                        lockupPrice={currentPrice}
                        priceSubtextFirst={offer.activityOffer?.price?.qualifierText}
                        a11yPrice={a11yPrice}
                        a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                        strikeThroughPrice={strikeThroughPrice}
                        strikeThroughTrigger={
                          <Link
                            to={{
                              pathname: routeStore.pathname,
                              search: routeStore.mergeQueryString(routeStore.search, {
                                pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}`,
                              }),
                            }}
                            className="disclaimerInfoIcon"
                          />
                        }
                      />
                      <QueryRoute query={{ pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}` }}>
                        <Layer id={`disclaimer-info-dialog-${campaignId}-${cardIndex}`} onDismiss={closeDialog}>
                          <InfoDialog
                            infoText="Descalaimer"
                            closeDialog={closeDialog}
                            buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
                          />
                        </Layer>
                      </QueryRoute>
                    </div>
                  </UitkLayoutPosition>
                </>
              </UitkLayoutGrid>
            </UitkCardAlohaContentSection>
            <UitkCardAlohaLink>
              {offer.activityOffer && (
                <TrackedLink
                  moduleName={moduleName}
                  action={Action.CLICK}
                  href={offer.activityOffer?.actionLink.href}
                  rfrr={`card-${campaignId}-${cardIndex}`}
                >
                  {offer.activityOffer?.actionLink.text}
                </TrackedLink>
              )}
            </UitkCardAlohaLink>
          </div>
        </UitkCardAloha>
      );
    }
  )
);
